import React, { useState, useEffect } from "react";
import {
  Layout,
  Card,
  Space,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Typography,
  message,
} from "antd";
import {
  FilterOutlined,
  UserOutlined,
  CheckCircleOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getSubscriptionStatistics } from "../../../api/statistics/statisticsRequest";
import { getSubscriptionTypes } from "../../../api/subscription_types/subscriptionTypesRequest";
import {
  statusTextSubscription,
  formatAmountInFrench,
} from "../../../utils/constants";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

const SubscriptionsStatistics = () => {
  const [dateRange, setDateRange] = useState(null);
  const [selectedSubscriptionType, setSelectedSubscriptionType] =
    useState(null);
  const [selectedGroupBy, setSelectedGroupBy] = useState(null);

  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [subscriptionsData, setSubscriptionsData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptionTypes();
  }, []);

  const filterSubscriptionsStatistics = async () => {
    setLoading(true);
    try {
      const data = await getSubscriptionStatistics({
        start_date:
          dateRange && dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : null,
        end_date:
          dateRange && dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : null,
        subscription_type_id: selectedSubscriptionType,
        group_by: selectedGroupBy,
      });
      setSubscriptionsData(data);
    } catch (error) {
      setSubscriptionsData(null);
      message.error("Échec de la récupération des données");
    } finally {
      setLoading(false);
    }
  };
  const fetchSubscriptionTypes = async () => {
    setLoading(true);
    try {
      const data = await getSubscriptionTypes();
      setSubscriptionTypes(data);
    } catch (error) {
      message.error("Échec de la récupération des types d'abonnements");
    } finally {
      setLoading(false);
    }
  }; // Timeline Chart Configuration
  const getTimelineChartOptions = () => {
    if (!subscriptionsData?.timeline) return {};

    const timelineData = subscriptionsData.timeline.map((item) => [
      new Date(item.date).getTime(),
      parseInt(item.count) || 0,
    ]);

    return {
      chart: {
        type: "line",
        height: 300,
      },
      title: {
        text: "Évolution des Abonnements",
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        type: "datetime",
        title: {
          text: "Période",
        },
      },
      yAxis: {
        title: {
          text: "Nombre d'Abonnements",
        },
        min: 0,
      },
      series: [
        {
          name: "Abonnements",
          data: timelineData,
          color: "#1890ff",
        },
      ],
      tooltip: {
        formatter: function () {
          return `<b>${this.series.name}</b><br/>${Highcharts.dateFormat(
            "%e %b %Y",
            this.x
          )}: ${this.y} abonnements`;
        },
      },
    };
  };
  // Status Distribution Pie Chart
  const getStatusChartOptions = () => {
    if (!subscriptionsData?.by_status) return {};

    const statusData = subscriptionsData.by_status.map((item) => ({
      name: statusTextSubscription(item.status),
      y: parseInt(item.count) || 0,
      color: getStatusColor(item.status),
    }));

    return {
      chart: {
        type: "pie",
        height: 300,
      },
      title: {
        text: "Répartition par Statut",
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          name: "Abonnements",
          data: statusData,
        },
      ],
      tooltip: {
        formatter: function () {
          return `<b>${this.point.name}</b><br/>${
            this.y
          } abonnements (${this.percentage.toFixed(1)}%)`;
        },
      },
    };
  };
  // Subscription Types Chart
  const getTypesChartOptions = () => {
    if (!subscriptionsData?.by_type) return {};

    const typesData = subscriptionsData.by_type.map((item) => ({
      name: item.type_name || "Type non défini",
      y: parseInt(item.count) || 0,
    }));

    return {
      chart: {
        type: "column",
        height: 300,
      },
      title: {
        text: "Répartition par Type d'Abonnement",
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        type: "category",
        title: {
          text: "Types d'Abonnement",
        },
      },
      yAxis: {
        title: {
          text: "Nombre d'Abonnements",
        },
        min: 0,
      },
      series: [
        {
          name: "Abonnements",
          data: typesData,
          color: "#52c41a",
        },
      ],
    };
  };

  // Zones Chart Configuration
  const getZonesChartOptions = () => {
    if (!subscriptionsData?.by_zone) return {};

    const zonesData = subscriptionsData.by_zone.map((item) => ({
      name: `${item.zone_name} (${item.zone_prefix})`,
      y: parseInt(item.subscription_count) || 0,
    }));

    return {
      chart: {
        type: "bar",
        height: 400,
      },
      title: {
        text: "Répartition par Zone",
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        type: "category",
        title: {
          text: "Zones",
        },
      },
      yAxis: {
        title: {
          text: "Nombre d'Abonnements",
        },
        min: 0,
      },
      series: [
        {
          name: "Abonnements",
          data: zonesData,
          color: "#722ed1",
        },
      ],
      tooltip: {
        formatter: function () {
          return `<b>${this.point.name}</b><br/>${this.y} abonnements`;
        },
      },
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#faad14",
      active: "#52c41a",
      approved: "#1890ff",
      rejected: "#ff4d4f",
      cancelled: "#ff7875",
      expired_pending: "#fa8c16",
    };
    return colors[status] || "#d9d9d9";
  };

  // Status Table Columns
  const statusColumns = [
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: getStatusColor(status),
              marginRight: 8,
            }}
          />
          {statusTextSubscription(status)}
        </div>
      ),
    },
    {
      title: "Nombre",
      dataIndex: "count",
      key: "count",
      align: "right",
      render: (count) => count.toLocaleString("fr-FR"),
    },
    {
      title: "Pourcentage",
      dataIndex: "percentage",
      key: "percentage",
      align: "right",
      render: (percentage) => `${percentage.toFixed(1)}%`,
    },
  ];
  // Subscription Types Table Columns
  const typesColumns = [
    {
      title: "Type d'Abonnement",
      dataIndex: "type_name",
      key: "type_name",
      render: (name) => name || "Type non défini",
    },
    {
      title: "Nombre",
      dataIndex: "count",
      key: "count",
      align: "right",
      render: (count) => parseInt(count).toLocaleString("fr-FR"),
    },
    {
      title: "Frais Totaux",
      dataIndex: "total_registration_fees",
      key: "total_registration_fees",
      align: "right",
      render: (fees) => formatAmountInFrench(parseFloat(fees) || 0),
    },
    {
      title: "Frais Payés",
      dataIndex: "total_registration_fees_paid",
      key: "total_registration_fees_paid",
      align: "right",
      render: (fees) => formatAmountInFrench(parseFloat(fees) || 0),
    },
  ];

  // Zones Table Columns
  const zonesColumns = [
    {
      title: "Zone",
      dataIndex: "zone_name",
      key: "zone_name",
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          <div style={{ fontSize: "12px", color: "#8c8c8c" }}>
            {record.zone_prefix}
          </div>
        </div>
      ),
    },
    {
      title: "Nombre d'Abonnements",
      dataIndex: "subscription_count",
      key: "subscription_count",
      align: "right",
      render: (count) => parseInt(count).toLocaleString("fr-FR"),
    },
    {
      title: "Montant Total",
      dataIndex: "total_amount",
      key: "total_amount",
      align: "right",
      render: (amount) => formatAmountInFrench(parseFloat(amount) || 0),
    },
  ];
  // Prepare table data
  const getStatusTableData = () => {
    if (!subscriptionsData?.by_status) return [];

    const total = subscriptionsData.by_status.reduce(
      (sum, item) => sum + parseInt(item.count),
      0
    );

    return subscriptionsData.by_status.map((item, index) => ({
      key: index,
      status: item.status,
      count: parseInt(item.count),
      percentage: total > 0 ? (parseInt(item.count) / total) * 100 : 0,
    }));
  };
  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Row gutter={16} align="middle">
              <Col xs={24} sm={12} md={6}>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="DD/MM/YYYY"
                  placeholder={["Date début", "Date fin"]}
                  className="w-full"
                />
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  value={selectedSubscriptionType}
                  onChange={setSelectedSubscriptionType}
                  placeholder="Type d'abonnement"
                  style={{ width: "100%" }}
                  allowClear
                >
                  {subscriptionTypes.map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Select
                  value={selectedGroupBy}
                  onChange={setSelectedGroupBy}
                  placeholder="Groupé par"
                  style={{ width: "100%" }}
                  allowClear
                >
                  <Option value="daily">Quotidien</Option>
                  <Option value="weekly">Hebdomadaire</Option>
                  <Option value="monthly">Mensuel</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={4}>
                <Button
                  type="primary"
                  icon={<FilterOutlined />}
                  onClick={filterSubscriptionsStatistics}
                  loading={loading}
                  className="w-full"
                >
                  Rechercher
                </Button>
              </Col>
            </Row>
          </Space>
        </Card>{" "}
        {subscriptionsData && (
          <Space direction="vertical" size="large" className="w-full">
            <Card className="shadow-sm">
              <Title level={2} className="text-center mb-6">
                Résumé des Abonnements
              </Title>
              <Row gutter={16} className="mb-6">
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Abonnements"
                    value={subscriptionsData.summary?.total || 0}
                    prefix={<UserOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Abonnements Actifs"
                    value={subscriptionsData.summary?.active || 0}
                    prefix={<CheckCircleOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Total Frais d'Inscription"
                    value={formatAmountInFrench(
                      parseFloat(
                        subscriptionsData.summary?.total_registration_fees
                      ) || 0
                    )}
                    prefix={<DollarOutlined />}
                  />
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Statistic
                    title="Frais Payés"
                    value={formatAmountInFrench(
                      parseFloat(
                        subscriptionsData.summary?.total_registration_fees_paid
                      ) || 0
                    )}
                    prefix={<DollarOutlined />}
                  />
                </Col>
              </Row>
            </Card>

            <Row gutter={16}>
              <Col xs={24}>
                <Card className="shadow-sm">
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={getTimelineChartOptions()}
                  />
                </Card>
              </Col>
            </Row>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Répartition par Statut
              </Title>
              <HighchartsReact
                highcharts={Highcharts}
                options={getStatusChartOptions()}
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Répartition par Type d'Abonnement
              </Title>
              <HighchartsReact
                highcharts={Highcharts}
                options={getTypesChartOptions()}
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Statistiques par Zone
              </Title>
              <HighchartsReact
                highcharts={Highcharts}
                options={getZonesChartOptions()}
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Détails des Statistiques par Statut
              </Title>
              <Table
                dataSource={getStatusTableData()}
                columns={statusColumns}
                pagination={false}
                rowKey="status"
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Synthèse des frais d’inscription par type d’abonnement
              </Title>
              <Table
                dataSource={subscriptionsData.by_type || []}
                columns={typesColumns}
                pagination={false}
                rowKey="type_name"
              />
            </Card>

            <Card className="shadow-sm mt-6">
              <Title level={3} className="mb-4">
                Détails par Zone
              </Title>
              <Table
                dataSource={subscriptionsData.by_zone || []}
                columns={zonesColumns}
                pagination={false}
                rowKey="zone_id"
              />
            </Card>
          </Space>
        )}
      </Content>
    </Layout>
  );
};

export default SubscriptionsStatistics;
